'use client';

import type {
  AiApplicationComponentsResponseDto,
  AiComponentDto,
} from '@terra/shared-contracts-ai-applications';
import { resolveAiComponentDisplayDescription } from '@terra/shared-contracts-ai-applications';
import {
  BodyDefault,
  Button,
  HeadingItem,
  MetaDefault,
  MonoSmall,
} from '@terra/ui-design-system';
import { Box, HStack, VStack } from '@terra/ui-styled-system';
import { useTranslations } from 'next-intl';
import {
  getAiComponentInputSchema,
  getAiComponentMetadataString,
} from '../utils/aiComponentMetadata';
import { resolveAiComponentVerificationBadge } from '../utils/aiComponentVerification';
import {
  compositionRelationLabel,
  resolveCompositionCardVisibility,
  resolveCompositionRoleText,
  resolveCompositionVisualKind,
  type CompositionVisualKind,
} from '../utils/compositionGraphModel';
import {
  COMPOSITION_HIDDEN_DOT,
  COMPOSITION_SHOWN_DOT,
  COMPOSITION_VISUAL_STYLE,
} from '../utils/compositionGraphVisual';

type AiApplicationCompositionDetailProps = {
  component: AiComponentDto;
  components: AiComponentDto[];
  relations: AiApplicationComponentsResponseDto['relations'];
  onSelectComponent: (componentId: string) => void;
};

export function AiApplicationCompositionDetail({
  component,
  components,
  relations,
  onSelectComponent,
}: AiApplicationCompositionDetailProps) {
  const t = useTranslations('aiApplications.componentDrawer');
  const tGraph = useTranslations('aiApplications.graph');
  const tShared = useTranslations('aiApplications.agentDrilldown');
  const tTypes = useTranslations('aiApplications.componentTypes');
  const tVerification = useTranslations('aiApplications.assets.verification');
  const tEvidence = useTranslations(
    'aiApplications.componentDrawer.verification',
  );
  const visualKind = resolveCompositionVisualKind(component);
  const visual = COMPOSITION_VISUAL_STYLE[visualKind];
  const Icon = visual.icon;
  const relatedRelations = relations.filter(
    (relation) =>
      relation.sourceId === component.id || relation.targetId === component.id,
  );
  const componentsById = new Map(
    components.map((item) => [item.id, item] as const),
  );
  const discriminator = getAiComponentMetadataString({
    metadata: component.metadata,
    field: 'discriminator',
  });
  const howToInvoke = getAiComponentMetadataString({
    metadata: component.metadata,
    field: 'stimulus',
  });
  const whatProvesIt = getAiComponentMetadataString({
    metadata: component.metadata,
    field: 'predictedSignal',
  });
  const visibility = resolveCompositionCardVisibility(component);
  const shown = visibility === 'shown' || visibility === 'verified';
  const visibilityLabel =
    visibility === 'shown'
      ? tEvidence('invocationUserVisible')
      : visibility === 'background'
        ? tEvidence('invocationHidden')
        : visibility === 'unknown'
          ? tEvidence('invocationUnknown')
          : visibility === 'verified'
            ? tVerification('verified')
            : null;
  const inputSchema = getAiComponentInputSchema({
    metadata: component.metadata,
    runtimeOverlay: component.runtimeOverlay,
  });
  const summary = resolveAiComponentDisplayDescription({
    description: component.description,
    metadata: component.metadata,
    type: component.type,
  });
  const updatedAt =
    component.updatedAt instanceof Date
      ? component.updatedAt
      : new Date(component.updatedAt);
  const kindLabel = visualKindLabel({
    kind: visualKind,
    graphLabel: tGraph,
    typeLabel: tTypes,
  });

  return (
    <VStack
      w="380px"
      flexShrink={0}
      h="full"
      minH={0}
      alignItems="stretch"
      gap={0}
      borderLeftWidth="1px"
      borderColor="border.neutral.subtle"
      bg="bg.default"
    >
      <VStack
        alignItems="stretch"
        gap="12px"
        px="20px"
        pt="20px"
        pb="16px"
        borderBottomWidth="1px"
        borderColor="border.neutral.subtle"
      >
        <MonoSmall color="fg.muted">{tGraph('context')}</MonoSmall>
        <HStack gap="12px" minW={0} alignItems="center">
          <Box
            w="36px"
            h="36px"
            flexShrink={0}
            borderRadius="8px"
            display="flex"
            alignItems="center"
            justifyContent="center"
            style={{
              background: visual.bg,
              color: visual.fg,
              boxShadow: `inset 0 0 0 1px ${visual.bd}`,
            }}
          >
            <Icon size={16} />
          </Box>
          <VStack gap="4px" minW={0} alignItems="flex-start">
            <HeadingItem lineHeight="1.2">{component.name}</HeadingItem>
            <MonoSmall
              letterSpacing="0.04em"
              lineHeight="1"
              style={{ color: visual.fg }}
            >
              {resolveCompositionRoleText(component)}
            </MonoSmall>
          </VStack>
        </HStack>
        {summary ? (
          <BodyDefault color="fg.default" lineHeight="1.5">
            {summary}
          </BodyDefault>
        ) : null}
      </VStack>
      <VStack
        flex={1}
        minH={0}
        overflow="auto"
        alignItems="stretch"
        gap="24px"
        p="20px"
      >
        <VStack alignItems="stretch" gap="10px">
          <MonoSmall color="fg.muted">{tShared('generalDetails')}</MonoSmall>
          <DetailRow label={tShared('type')} value={kindLabel} />
          <DetailRow
            label={tShared('role')}
            value={resolveCompositionRoleText(component)}
          />
          <DetailRow
            label={t('provenance')}
            value={
              component.provenance
                ? t(`provenanceValues.${component.provenance}`)
                : tShared('unknownRole')
            }
          />
          <DetailRow
            label={t('verificationStatus')}
            value={tVerification(
              resolveAiComponentVerificationBadge({ component }),
            )}
          />
          <DetailRow
            label={tShared('coverage')}
            value={
              component.isInternal ? tShared('internal') : tShared('external')
            }
          />
          <DetailRow
            label={tShared('relationships')}
            value={String(relatedRelations.length)}
          />
          <DetailRow
            label={tShared('updated')}
            value={
              Number.isNaN(updatedAt.getTime())
                ? tShared('unknownRole')
                : updatedAt.toLocaleDateString()
            }
          />
        </VStack>
        {discriminator || howToInvoke || whatProvesIt || visibilityLabel ? (
          <VStack alignItems="stretch" gap="14px">
            <MonoSmall color="fg.muted">{t('codeAnalysis')}</MonoSmall>
            {discriminator ? (
              <AnalysisBlock title={t('discriminator')} value={discriminator} />
            ) : null}
            {howToInvoke ? (
              <AnalysisBlock
                title={t('howToInvoke')}
                value={howToInvoke}
                mono
              />
            ) : null}
            {whatProvesIt ? (
              <AnalysisBlock
                title={t('whatProvesIt')}
                value={whatProvesIt}
                mono
              />
            ) : null}
            {visibilityLabel ? (
              <VStack alignItems="stretch" gap="4px">
                <MonoSmall color="fg.muted">
                  {t('invocationVisibility')}
                </MonoSmall>
                <HStack gap="8px" alignItems="center">
                  <Box
                    w="6px"
                    h="6px"
                    borderRadius="full"
                    flexShrink={0}
                    style={{
                      background: shown
                        ? COMPOSITION_SHOWN_DOT
                        : COMPOSITION_HIDDEN_DOT,
                    }}
                  />
                  <MonoSmall color="fg.default">{visibilityLabel}</MonoSmall>
                </HStack>
              </VStack>
            ) : null}
          </VStack>
        ) : null}
        {inputSchema !== null ? (
          <VStack alignItems="stretch" gap="10px">
            <MonoSmall color="fg.muted">{t('inputSchema')}</MonoSmall>
            <Box
              bg="bg.canvas"
              borderWidth="1px"
              borderColor="border.neutral.subtle"
              borderRadius="8px"
              p="12px"
              overflow="auto"
            >
              <MonoSmall whiteSpace="pre" color="fg.muted">
                {JSON.stringify(inputSchema, null, 2)}
              </MonoSmall>
            </Box>
          </VStack>
        ) : null}
        <VStack alignItems="stretch" gap="8px">
          <MonoSmall color="fg.muted">{t('relatedComponents')}</MonoSmall>
          {relatedRelations.length === 0 ? (
            <MonoSmall color="fg.muted">{t('noRelatedComponents')}</MonoSmall>
          ) : (
            relatedRelations.map((relation) => {
              const otherId =
                relation.sourceId === component.id
                  ? relation.targetId
                  : relation.sourceId;
              const other = componentsById.get(otherId);

              if (!other) {
                return null;
              }

              const otherVisual =
                COMPOSITION_VISUAL_STYLE[resolveCompositionVisualKind(other)];
              const OtherIcon = otherVisual.icon;
              const direction =
                relation.sourceId === component.id ? '→ ' : '← ';

              return (
                <Button
                  key={relation.id}
                  variant="outline"
                  size="sm"
                  w="full"
                  h="auto"
                  py="8px"
                  px="10px"
                  justifyContent="flex-start"
                  onClick={() => onSelectComponent(other.id)}
                >
                  <HStack gap="10px" w="full" minW={0} alignItems="center">
                    <Box
                      w="24px"
                      h="24px"
                      flexShrink={0}
                      borderRadius="6px"
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      style={{
                        background: otherVisual.bg,
                        color: otherVisual.fg,
                        boxShadow: `inset 0 0 0 1px ${otherVisual.bd}`,
                      }}
                    >
                      <OtherIcon size={12} />
                    </Box>
                    <VStack gap="2px" flex={1} minW={0} alignItems="flex-start">
                      <MonoSmall lineClamp={1} color="fg.default">
                        {other.name}
                      </MonoSmall>
                      <MonoSmall lineClamp={1} color="fg.muted">
                        {`${direction}${compositionRelationLabel(relation)}`}
                      </MonoSmall>
                    </VStack>
                  </HStack>
                </Button>
              );
            })
          )}
        </VStack>
      </VStack>
    </VStack>
  );
}

function DetailRow({ label, value }: { label: string; value: string }) {
  return (
    <Box display="grid" gridTemplateColumns="120px minmax(0, 1fr)" gap="12px">
      <MetaDefault color="fg.muted">{label}</MetaDefault>
      <BodyDefault color="fg.default" overflowWrap="anywhere">
        {value}
      </BodyDefault>
    </Box>
  );
}

function AnalysisBlock({
  title,
  value,
  mono,
}: {
  title: string;
  value: string;
  mono?: boolean;
}) {
  return (
    <VStack alignItems="stretch" gap="4px">
      <MonoSmall color="fg.muted">{title}</MonoSmall>
      {mono ? (
        <Box
          bg="bg.canvas"
          borderWidth="1px"
          borderColor="border.neutral.subtle"
          borderRadius="6px"
          px="10px"
          py="8px"
        >
          <MonoSmall
            color="fg.default"
            lineHeight="1.5"
            overflowWrap="anywhere"
          >
            {value}
          </MonoSmall>
        </Box>
      ) : (
        <BodyDefault color="fg.default" lineHeight="1.5">
          {value}
        </BodyDefault>
      )}
    </VStack>
  );
}

function visualKindLabel({
  kind,
  graphLabel,
  typeLabel,
}: {
  kind: CompositionVisualKind;
  graphLabel: (
    key:
      | 'kinds.router'
      | 'kinds.middleware'
      | 'kinds.toolFunction'
      | 'kinds.hostedTool',
  ) => string;
  typeLabel: (key: 'agent' | 'memory' | 'tool') => string;
}): string {
  switch (kind) {
    case 'AGENT':
      return typeLabel('agent');
    case 'ROUTER':
      return graphLabel('kinds.router');
    case 'MIDDLEWARE':
      return graphLabel('kinds.middleware');
    case 'MEMORY':
      return typeLabel('memory');
    case 'TOOL_FUNCTION':
      return graphLabel('kinds.toolFunction');
    case 'TOOL_HOSTED':
      return graphLabel('kinds.hostedTool');
    case 'TOOL':
      return typeLabel('tool');
  }
}
